/**
 * @fileoverview Avatar and fields shared by the user creation page and dialog.
 * @module components/userCreation/UserCreationFields
 */

import AssetImage from "../AssetImage";
import { selectedCreatableRole } from "../../logic/user/creatableRoleSelection.js";
import Select from "react-select";
import { ReactComponent as DeleteIcon } from "../../assets/icons/UserViews/DeleteIcon.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/UserViews/EditIcon.svg";
import { ReactComponent as UserNameIcon } from "../../assets/icons/UserViews/UserNameIcon.svg";
import { ReactComponent as TitleIcon } from "../../assets/icons/UserViews/TitleIcon.svg";
import { ReactComponent as EmailIcon } from "../../assets/icons/UserViews/EmailIcon.svg";
import { ReactComponent as RFCIcon } from "../../assets/icons/UserViews/RFCIcon.svg";
import {
  displayedRfc,
  errorBorderColor,
  rfcSourceForUser,
} from "./userCreationFormModel";

/**
 * Shown only while the field has a value, matching the original conditional button.
 * @param {Object} props
 * @param {boolean} props.visible
 * @param {string} props.className
 * @param {Function} props.onClear
 * @returns {JSX.Element|null}
 */
const ClearButton = ({ visible, className, onClear }) => {
  if (!visible) {
    return null;
  }
  return (
    <button className={className} onClick={onClear}>
      &times;
    </button>
  );
};

/**
 * Profile photo, or the first letter of the name when no photo is stored.
 * JPEG, PNG, and WEBP still come through the hidden file input.
 * @param {Object} props
 * @param {Object} props.form
 * @param {Object} props.classes
 * @param {string} props.alt
 * @returns {JSX.Element}
 */
export const UserCreationAvatar = ({ form, classes, alt }) => (
  <>
    <div className={classes.avatar}>
      {form.imagenDePerfil ? (
        <AssetImage
          src={form.imagenDePerfil}
          alt={alt}
          className={classes.avatarImage}
        />
      ) : (
        form.nombre.charAt(0)
      )}
    </div>
    <div className={classes.avatarButtons}>
      <button className={classes.editButton} onClick={form.handleAvatarEditClick}>
        <EditIcon />
      </button>
      <button className={classes.deleteButton} onClick={form.handleDeleteAvatar}>
        <DeleteIcon />
      </button>
    </div>
    <input
      type="file"
      ref={form.fileInputRef}
      style={{ display: "none" }}
      accept="image/jpeg, image/png, image/webp"
      onChange={form.handleAvatarChange}
    />
  </>
);

/**
 * One labeled text field. The full page wraps it in a section. The dialog does not,
 * so the label stays a direct child of the modal. Name and title omit `borderColor`,
 * which leaves the input without an inline border style.
 * @param {Object} props
 * @param {string} props.label
 * @param {JSX.Element} [props.icon] Already wrapped, because the page and dialog use different icon classes.
 * @param {string} props.type
 * @param {string} props.placeholder
 * @param {string} props.value
 * @param {Function} props.onChange
 * @param {Function} [props.onBlur]
 * @param {Object} props.className
 * @param {string} [props.borderColor]
 * @param {string} props.clearClassName
 * @param {Function} [props.onClear]
 * @param {boolean} [props.disabled]
 * @param {string} props.wrapperClassName
 * @param {string} props.containerClassName
 * @param {string} [props.sectionClassName]
 * @returns {JSX.Element}
 */
const LabeledInput = ({
  label,
  icon,
  type,
  placeholder,
  value,
  onChange,
  onBlur,
  className,
  borderColor,
  clearClassName,
  onClear,
  disabled,
  wrapperClassName,
  containerClassName,
  sectionClassName,
}) => {
  const field = (
    <>
      <label className={className.label}>{label}</label>
      <div className={containerClassName}>
        <div className={wrapperClassName}>
          {icon}
          <input
            type={type}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            className={className.input}
            style={borderColor === undefined ? undefined : { borderColor }}
            disabled={disabled}
          />
          <ClearButton
            visible={Boolean(onClear) && Boolean(value)}
            className={clearClassName}
            onClear={onClear}
          />
        </div>
      </div>
    </>
  );

  if (!sectionClassName) {
    return field;
  }
  return <div className={sectionClassName}>{field}</div>;
};

/**
 * Role, name, and title. The role list stays disabled when only one role can be
 * assigned, or when the account is not a company member.
 * @param {Object} props
 * @param {Object} props.form
 * @param {Object} props.classes
 * @param {Object} props.selectStyles
 * @param {string} props.i18nPrefix
 * @returns {JSX.Element}
 */
export const UserCreationIdentityFields = ({
  form,
  classes,
  selectStyles,
  i18nPrefix,
}) => {
  const roleField = (
    <>
      <label className={classes.label}>{form.t(`${i18nPrefix}.userType`)}</label>
      <Select
        options={form.roles}
        value={selectedCreatableRole(form.roles, form.tipoDeUsuario)}
        onChange={form.handleRoleChange}
        placeholder={form.t(`${i18nPrefix}.userTypePlaceholder`)}
        isDisabled={form.userTypeLocked}
        styles={selectStyles}
      />
    </>
  );

  return (
    <>
      {classes.section ? <div className={classes.section}>{roleField}</div> : roleField}
      <LabeledInput
        label={form.t(`${i18nPrefix}.userName`)}
        icon={
          <div className={classes.inputIcon}>
            <UserNameIcon />
          </div>
        }
        type="text"
        placeholder={form.t(`${i18nPrefix}.userNamePlaceholder`)}
        value={form.nombre}
        onChange={form.handleNameChange}
        className={classes}
        clearClassName={classes.inputClearButton}
        onClear={form.nombre ? form.clearName : undefined}
        containerClassName={classes.inputContainer}
        wrapperClassName={classes.inputWrapper}
        sectionClassName={classes.section || undefined}
      />
      <hr className={classes.divider} />
      <LabeledInput
        label={form.t(`${i18nPrefix}.title`)}
        icon={
          <div className={classes.inputIcon}>
            <TitleIcon />
          </div>
        }
        type="text"
        placeholder={form.t(`${i18nPrefix}.titlePlaceholder`)}
        value={form.titulo}
        onChange={form.handleTitleChange}
        className={classes}
        clearClassName={classes.inputClearButton}
        onClear={form.titulo ? form.clearTitle : undefined}
        containerClassName={classes.inputContainer}
        wrapperClassName={classes.inputWrapper}
        sectionClassName={classes.section || undefined}
      />
    </>
  );
};

/**
 * Email, phone, and RFC. Phone has no icon. A company member types the RFC.
 * Any other account sees the company RFC and cannot clear or edit it.
 * The red border uses that same RFC string the blur check validates.
 * @param {Object} props
 * @param {Object} props.form
 * @param {Object} [props.user]
 * @param {Object} props.classes
 * @param {string} props.i18nPrefix
 * @returns {JSX.Element}
 */
export const UserCreationContactFields = ({ form, user, classes, i18nPrefix }) => {
  const rfcSource = rfcSourceForUser(user, form.RFC);
  const emailBorder = errorBorderColor(
    form.correoElectronico.trim() !== "" && (form.emailError || form.emailFormatError),
  );
  const phoneBorder = errorBorderColor(
    form.numeroCelular.trim() !== "" && form.phoneFormatError,
  );
  const rfcBorder = errorBorderColor(
    rfcSource.trim() !== "" && (form.RFCError || form.rfcFormatError),
  );

  return (
    <>
      <LabeledInput
        label={form.t(`${i18nPrefix}.email`)}
        icon={
          <div className={classes.inputIcon}>
            <EmailIcon />
          </div>
        }
        type="text"
        placeholder={form.t(`${i18nPrefix}.emailPlaceholder`)}
        value={form.correoElectronico}
        onChange={form.handleEmailChange}
        onBlur={form.handleEmailBlur}
        className={classes}
        borderColor={emailBorder}
        clearClassName={classes.inputClearButton}
        onClear={form.correoElectronico ? form.clearEmail : undefined}
        containerClassName={classes.inputContainer}
        wrapperClassName={classes.inputWrapper}
        sectionClassName={classes.section || undefined}
      />
      <LabeledInput
        label={form.t(`${i18nPrefix}.phone`)}
        type="tel"
        placeholder={form.t(`${i18nPrefix}.phonePlaceholder`)}
        value={form.numeroCelular}
        onChange={form.handlePhoneChange}
        onBlur={form.handlePhoneBlur}
        className={classes}
        borderColor={phoneBorder}
        clearClassName={classes.inputClearButton}
        onClear={form.numeroCelular ? form.clearPhone : undefined}
        containerClassName={classes.inputContainer}
        wrapperClassName={classes.inputWrapper}
        sectionClassName={classes.section || undefined}
      />
      <LabeledInput
        label={form.t(`${i18nPrefix}.rfc`)}
        icon={
          <div className={classes.inputIcon}>
            <RFCIcon />
          </div>
        }
        type="text"
        placeholder={form.t(`${i18nPrefix}.rfcPlaceholder`)}
        value={displayedRfc(user, form.RFC)}
        onChange={form.handleRfcChange}
        onBlur={form.handleRfcBlur}
        className={classes}
        borderColor={rfcBorder}
        clearClassName={classes.inputClearButton}
        onClear={user?.esColaborador && form.RFC ? form.clearRfc : undefined}
        disabled={!user?.esColaborador}
        containerClassName={classes.inputContainer}
        wrapperClassName={classes.inputWrapper}
        sectionClassName={classes.section || undefined}
      />
    </>
  );
};
